/**
 * Platform Admin Shell V0.2 (spec §7/§8/§59) — the full target Sidebar
 * structure, but only items with a real, already-working route are
 * `enabled`. Everything else is `disabled: true` — rendered (so the
 * eventual full IA is visible), but not clickable and never a 404 or
 * fake interaction (spec §59: "不要 onclick => alert('todo')").
 *
 * Deliberately a plain data table, not 20 placeholder React Route
 * components (spec §8) — a disabled row never needs a route at all.
 */

export interface PlatformNavItem {
  key: string
  label: string
  /** When set, sidebar label is resolved via `platformAdmin` i18n instead of `label`. */
  labelKey?: string
  /** Present only for enabled items — used for the router Link + the
   * "current section" highlight. */
  path?: string
  disabled?: boolean
}

export interface PlatformNavGroup {
  key: string
  /** Collapsible group label — every group has a title (no orphan top-level pages). */
  title: string
  /** All groups default expanded on first Platform Admin entry (P1 sidebar spec). */
  defaultExpanded?: boolean
  items: PlatformNavItem[]
}

/** Flatten every sidebar item for route/menu completeness checks. */
export function flattenPlatformNavItems(nav: PlatformNavGroup[] = PLATFORM_NAV): PlatformNavItem[] {
  return nav.flatMap((group) => group.items)
}

/** Enabled, routable sidebar entries (disabled rows and path-less rows excluded). */
export function collectPlatformNavRoutes(nav: PlatformNavGroup[] = PLATFORM_NAV): string[] {
  return flattenPlatformNavItems(nav)
    .filter((item) => Boolean(item.path) && !item.disabled)
    .map((item) => item.path as string)
}

/** Group keys that participate in expand/collapse (every configured group). */
export function collectPlatformNavGroupKeys(nav: PlatformNavGroup[] = PLATFORM_NAV): string[] {
  return nav.map((group) => group.key)
}

/** Initial expanded set: all groups with `defaultExpanded !== false`. */
export function initialExpandedPlatformNavGroups(nav: PlatformNavGroup[] = PLATFORM_NAV): Set<string> {
  return new Set(nav.filter((group) => group.defaultExpanded !== false).map((group) => group.key))
}

export const PLATFORM_NAV: PlatformNavGroup[] = [
  {
    key: 'root',
    title: '概览',
    defaultExpanded: true,
    items: [
      // VISTOERA_PLATFORM_ADMIN_MARKETING_ANALYTICS_HOME_AND_TRACKING_
      // FOUNDATION_V1 (task spec §4/§6) — new Platform Admin default home,
      // now first in the Sidebar; 平台概览 keeps its own route/position
      // right below it (task spec §5: not deleted, not demoted out of the
      // top-level group). Same plain-hardcoded-Chinese-label convention as
      // every other row in this file (task spec §6: no large Sidebar
      // i18n refactor this round) — the Marketing Analytics *page itself*
      // is fully zh-CN/en-US (see `../../i18n/locales/*/marketingAnalytics.json`).
      { key: 'marketing-analytics', label: '营销分析', path: '/platform/marketing-analytics' },
      { key: 'overview', label: '平台概览', path: '/platform/overview' },
    ],
  },
  {
    key: 'tenant',
    title: '租户管理',
    defaultExpanded: true,
    items: [
      { key: 'tenants', label: '租户列表', path: '/platform/tenants' },
      { key: 'sites', label: '站点管理', path: '/platform/sites' },
      { key: 'domains', label: '域名管理', path: '/platform/domains' },
      { key: 'tenant-plans', label: '租户套餐', path: '/platform/tenant-plans' },
      { key: 'tenant-quota', label: '配额管理', disabled: true },
    ],
  },
  {
    key: 'resource',
    title: '资源监控',
    defaultExpanded: true,
    items: [
      { key: 'workers', label: 'Worker 节点', path: '/platform/workers' },
      { key: 'agent-runtimes', label: 'Agent Runtime', path: '/platform/agent-runtimes' },
      // VISTOERA_EXTERNAL_INFRA_OPERATIONS_V1 — real R2 usage breakdown/
      // top-consumers/orphan-audit/cleanup-dry-run + Render Backend/
      // Database detail, never just a raw byte count.
      { key: 'storage', label: '存储运维', path: '/platform/storage' },
      { key: 'cdn', label: 'CDN 统计', disabled: true },
      { key: 'bandwidth', label: '带宽监控', disabled: true },
    ],
  },
  {
    key: 'ai',
    title: 'AI 管理',
    defaultExpanded: true,
    items: [
      // VISTOERA_PLATFORM_NODE_PROVIDER_MAPPING_AND_EXECUTION_TARGET_
      // MANAGEMENT_V1 — Node/Capability-centric Provider Option admin
      // (Node Icon Rail + Provider table). Sibling to Skill 库 / Engine
      // 配置; never a second design system.
      { key: 'providers', label: '功能节点管理', labelKey: 'providers.navLabel', path: '/platform/providers' },
      // Provider Credential Center / Secret Vault Foundation V1 —
      // encrypted credential vault (never a plaintext key list). Path is
      // deliberately NOT /platform/providers (that route is Node Provider
      // Mapping above). Registry metadata lives at credential-providers.
      { key: 'credential-providers', label: 'Provider 管理', path: '/platform/credential-providers' },
      { key: 'provider-credentials', label: 'Credentials', path: '/platform/provider-credentials' },
      { key: 'skills', label: 'Skill 库', path: '/platform/skills' },
      { key: 'engine-profiles', label: '', labelKey: 'engineProfiles.navLabel', path: '/platform/engine-profiles' },
      { key: 'models', label: '模型管理', path: '/platform/models' },
      // VISTOERA_MODEL_CAPABILITY_REGISTRY_OFFICIAL_SYNC_ADMIN_OVERRIDE_
      // EFFECTIVE_RESOLUTION_AND_FUNCTION_NODE_MODEL_SELECTION_P0_V1 (task
      // spec §5) — canonical product name is "模型能力" / "Model
      // Capabilities", never "模型能力矩阵" (the matrix is only one tab
      // inside a model's detail).
      { key: 'model-capabilities', label: '模型能力', path: '/platform/model-capabilities' },
      { key: 'tools', label: '工具管理', disabled: true },
      { key: 'knowledge', label: '知识库管理', disabled: true },
      { key: 'ai-chat-tasks', label: 'AI 对话任务', path: '/platform/ai-chat/tasks' },
      { key: 'ai-conversations', label: 'AI 会话', path: '/platform/ai-chat/conversations' },
    ],
  },
  {
    key: 'tasks',
    title: '任务队列',
    defaultExpanded: true,
    items: [
      { key: 'task-queue', label: '任务队列', disabled: true },
      { key: 'task-history', label: '任务历史', disabled: true },
      { key: 'task-failed', label: '失败任务', disabled: true },
    ],
  },
  {
    key: 'system',
    title: '系统管理',
    defaultExpanded: true,
    items: [
      { key: 'system-settings', label: '系统设置', path: '/platform/system-settings' },
      // External Services / Infrastructure Registry (P0-B) — real
      // third-party dependency audit (Cloudflare R2/DNS/Registrar, Render
      // Backend/PostgreSQL, Resend Email, GitHub), never fabricated.
      { key: 'external-services', label: '外部服务', path: '/platform/external-services' },
      { key: 'audit-log', label: '操作日志', disabled: true },
      { key: 'security', label: '安全管理', path: '/platform/security' },
      { key: 'announcements', label: '公告管理', disabled: true },
    ],
  },
  {
    key: 'billing',
    title: '计费管理',
    defaultExpanded: true,
    items: [
      // Plan + Credits + Metering V1 (Stage S4) — Rate Rules (Stage S2)
      // + read-only Plan Catalog (Stage S1), both admin-editable/-viewable
      // from this one page.
      // VISTOERA_BILLING_PLAN_CREDITS_TOPUP_PAYG_REWARDS_AND_PROMOTIONS_
      // FOUNDATION_P0_V1 (task spec §17) — two Sidebar entries, ONE
      // underlying route/page (`PlanCreditsBillingPage`, 5 internal
      // tabs — task spec §0's "同一个 Billing Domain"), differing only
      // in which tab they land on by default via `?tab=...`. Placed
      // ABOVE the legacy "费率与套餐" row (task spec §16 —
      // LEGACY_PLAN_CATALOG_STRATEGY: legacy page kept read-only/
      // reachable, never a second editable Plan entry point).
      { key: 'plan-credits', label: '套餐与积分', path: '/platform/plan-credits?tab=plans' },
      // VistoeraTASK-00631 — plan node/channel defaults (Phase 1 APIs); under 套餐管理.
      { key: 'plan-auth-matrix', label: '套餐功能授权', path: '/platform/plan-auth-matrix' },
      { key: 'topup-payg', label: '按量计费与充值', path: '/platform/plan-credits?tab=topup-payg' },
      { key: 'rate-rules', label: '费率与套餐（旧，只读套餐目录）', path: '/platform/rate-rules' },
      // VISTOERA_AI_BILLING_PHASE2_FULL_RATE_CATALOG_USAGE_LEDGER_AND_
      // PRICING_V1 (task spec §25) — the fine-grained AI Rate Catalog V2
      // (saas_rate_catalog), sibling to (never replacing) the 4-row
      // legacy Rate Rules page above.
      { key: 'ai-billing-rules', label: 'AI 计费规则', path: '/platform/ai-billing-rules' },
      // VISTOERA_AI_COST_PRICING_PROVIDER_COST_POINTS_REVENUE_MARGIN_
      // CONTROL_PLANE_P0_V1 — cost / points / revenue / margin control
      // plane over the SAME saas_rate_catalog (never a parallel table).
      { key: 'ai-cost-pricing', label: 'AI 成本与定价', path: '/platform/ai-cost-pricing' },
      { key: 'subscriptions', label: '订阅管理', disabled: true },
      { key: 'invoices', label: '账单管理', disabled: true },
    ],
  },
  {
    key: 'support',
    title: '支持管理',
    defaultExpanded: true,
    items: [{ key: 'tickets', label: '工单管理', disabled: true }],
  },
]
